/**
 * Change password — in this app.
 *
 * ★ This screen is the reason the `mustChangePassword` branch does not link
 *   anywhere else. RocketRep owns its own passwords now (Edward, 2026-09-16);
 *   sending somebody to LaaS to change a RocketRep password would be the old,
 *   reversed design creeping back in through a hyperlink.
 *
 * Two ways in, and they read differently on purpose:
 *   · FORCED — the password was issued, not chosen. Nothing else in the product
 *     opens until it is replaced, and the screen says why rather than trapping
 *     them on a form with no explanation.
 *   · VOLUNTARY — a signed-in person changing their own password, with a way
 *     back to where they were.
 *
 * The rule text is stated BEFORE they type, not after the server rejects them.
 */

import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { AuthShell, Booting, Message, Submit } from "./chrome";
import { audienceOfPath, homeFor } from "./types";

/** Stated on screen, and checked here so a weak one never reaches the wire. */
const MIN_LENGTH = 10;

export default function ChangePasswordScreen() {
  const { status, person, mustChangePassword, changePassword, logout } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (status === "checking") return <Booting />;
  if (status === "unreachable") {
    return (
      <AuthShell
        eyebrow="RocketRep · Aspira"
        title="Can't reach RocketRep"
        sub="Try again in a moment."
      >
        <div className="rra-btns">
          <button type="button" className="rra-cta-s" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </AuthShell>
    );
  }
  // No session at all — there is no password here to change.
  if (status !== "signed-in") return <Navigate to="/login" replace />;

  const home = person ? homeFor(person) : "/";

  if (done) {
    return (
      <AuthShell
        eyebrow="RocketRep · Aspira"
        title="Password changed"
        sub="Use it next time you sign in."
      >
        <Message tone="ok" heading="Done">
          Signed in elsewhere? Sign out there and back in.
        </Message>
        <div className="rra-btns">
          <button
            type="button"
            className="rra-cta-s"
            onClick={() => navigate(home, { replace: true })}
          >
            Continue to {audienceOfPath(home) === "pro" ? "the field app" : "the dashboard"}
          </button>
        </div>
      </AuthShell>
    );
  }

  const problems = validate(current, next, confirm);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const found = validate(current, next, confirm);
    if (found) {
      setError(found);
      return;
    }
    setBusy(true);
    setError(null);
    const out = await changePassword(current, next);
    setBusy(false);

    switch (out.kind) {
      case "ok":
        setCurrent("");
        setNext("");
        setConfirm("");
        setDone(true);
        return;
      case "rejected":
        setError(out.message);
        return;
      case "signed-out":
        setError(null);
        return;
      case "unreachable":
        setError(out.message);
        return;
    }
  }

  return (
    <AuthShell
      eyebrow="RocketRep · Aspira"
      title={mustChangePassword ? "Choose your password" : "Change your password"}
      sub={
        mustChangePassword ? (
          <>
            Choose your own password to continue.
          </>
        ) : (
          <>
            Signed in as <b>{person?.email || person?.name}</b>.
          </>
        )
      }
      foot={
        mustChangePassword ? (
          <>
            Not you? <b>Sign out</b> below and start again.
          </>
        ) : null
      }
    >
      <form className="rra-form" onSubmit={onSubmit} noValidate>
        <label className="rra-field">
          <span className="rra-k">{mustChangePassword ? "Issued password" : "Current password"}</span>
          <span className="rra-inp-wrap">
            <input
              className="rra-inp"
              type={reveal ? "text" : "password"}
              autoComplete="current-password"
              value={current}
              disabled={busy}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </span>
        </label>

        <label className="rra-field">
          <span className="rra-k">New password</span>
          <span className="rra-inp-wrap">
            <input
              className="rra-inp rra-inp-eye"
              type={reveal ? "text" : "password"}
              autoComplete="new-password"
              value={next}
              disabled={busy}
              onChange={(e) => setNext(e.target.value)}
            />
            <button
              type="button"
              className="rra-eye"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? "Hide passwords" : "Show passwords"}
            >
              {reveal ? "Hide" : "Show"}
            </button>
          </span>
          <span className="rra-hint">
            At least {MIN_LENGTH} characters, not the one you were issued.
          </span>
        </label>

        <label className="rra-field">
          <span className="rra-k">New password again</span>
          <span className="rra-inp-wrap">
            <input
              className={`rra-inp${confirm && confirm !== next ? " rra-inp-bad" : ""}`}
              type={reveal ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              disabled={busy}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </span>
        </label>

        <Submit busy={busy} disabled={problems !== null} busyLabel="Saving">
          Save new password
        </Submit>
      </form>

      {error ? (
        <Message tone="bad" heading="Not changed">
          {error}
        </Message>
      ) : null}

      {problems && (current || next || confirm) ? (
        <Message heading="Before you can save">{problems}</Message>
      ) : null}

      <div className="rra-btns">
        {mustChangePassword ? (
          <button type="button" className="rra-cta-s" onClick={() => void logout()}>
            Sign out
          </button>
        ) : (
          <button type="button" className="rra-cta-s" onClick={() => navigate(home)}>
            Back
          </button>
        )}
      </div>
    </AuthShell>
  );
}

/** The first thing wrong, in the order a person would notice it. Null = fine. */
function validate(current: string, next: string, confirm: string): string | null {
  if (!current) return "Enter the password you signed in with.";
  if (!next) return "Choose a new password.";
  if (next.length < MIN_LENGTH) {
    return `The new password needs at least ${MIN_LENGTH} characters — this one has ${next.length}.`;
  }
  if (next === current) return "The new password has to be different from the old one.";
  if (!confirm) return "Type the new password a second time.";
  if (next !== confirm) return "The two new passwords do not match.";
  return null;
}
